import { useMediaQuery } from '@material-ui/core'
import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'
import { useTranslation } from 'react-i18next'
import Joyride, {
	ACTIONS,
	type CallBackProps,
	EVENTS,
	STATUS,
	type Step,
} from 'react-joyride'
import { useLocation, useNavigate } from 'react-router'
import WalkthroughTooltip from 'src/components/ui/Walkthrough/WalkthroughTooltip'
import { useFetchUserInfoQuery } from 'src/redux/api/graphql/userInfoApi'
import {
	setAdminTour,
	setIsAdminWalkthroughClosed,
	setIsMenuDrawerBudgetingOpen,
	setIsMenuDrawerExpensesOpen,
	setIsMenuDrawerOpen,
	setIsMenuDrawerRevenuesOpen,
	setIsUserMenuOpen,
	updateUserInfo,
} from 'src/redux/slices/settings'
import {
	useAppDispatch,
	useAppSelector,
	useAppThunkDispatch,
} from 'src/utils/hooks/reduxHooks'

const AdminWalkthrough = () => {
	const { pathname } = useLocation()
	const navigate = useNavigate()
	const isTeamsPage = pathname.includes('/clients')
	const isProjectsPage = pathname.includes('/projects')

	const { t, i18n } = useTranslation()
	const { t: tAdminwalkthrough } = useTranslation('adminwalkthrough')
	const language = i18n.language
	const dispatch = useAppDispatch()
	const dispatchThunk = useAppThunkDispatch()

	const [showConfetti, setShowConfetti] = useState(false)
	const [dontShowTour, setDontShowTour] = useState(false)

	const userInfoId = useAppSelector((state) => state.auth.userInfo?.id)
	const { data: userInfo } = useFetchUserInfoQuery(
		{
			userInfoId: userInfoId!,
		},
		{
			skip: !userInfoId,
		}
	)
	const isUserMenuOpen = useAppSelector(
		(state) => state.settings.isUserMenuOpen
	)
	const isMenuDrawerOpen = useAppSelector(
		(state) => state.settings.isMenuDrawerOpen
	)
	const userInformation = useAppSelector(
		(state) => state.settings.userInformation
	)
	const isMenuDrawerRevenuesOpen = useAppSelector(
		(state) => state.settings.isMenuDrawerRevenuesOpen
	)
	const adminTour = useAppSelector((state) => state.settings.adminTour)
	const projectChildrenData = useAppSelector(
		(state) => state.projects.projectChildrenData
	)
	const fetchProjectChildrenApiStatus = useAppSelector(
		(state) => state.projects.fetchProjectChildrenApiStatus
	)

	const isSmall = useMediaQuery('(max-width: 1090px)')
	const hideTour = userInformation?.hideTour
	const isAdmin = userInfo?.cognitoGroup?.includes('companyAdmin')

	const handleChange = () => {
		setDontShowTour(!dontShowTour)
	}
	const newSteps: Step[] = [
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.startingSlide')}</p>
				</>
			),
			placement: 'center',
			//disableBeacon: true,
			target: 'body',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.slide2')}</p>
				</>
			),
			placement: 'center',
			//disableBeacon: true,
			target: 'body',
		},
		{
			content: (
				<>
					<p>
						{tAdminwalkthrough('steps.accountSettingsContentSlide')}
					</p>
				</>
			),
			placement: 'left',
			//disableBeacon: true,
			target: '.settingsButtonContent',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.menuDrawerSlide')}</p>
				</>
			),
			placement: 'center',
			//disableBeacon: true,
			target: '.menuDrawer',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.menuDrawerBudgetingSlide')}</p>
				</>
			),
			placement: 'right',
			//disableBeacon: true,
			target: '.menuDrawerBudgeting',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.menuDrawerExpensesSlide')}</p>
				</>
			),
			placement: 'right',
			//disableBeacon: true,
			target: '.menuDrawerExpenses',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.menuDrawerRevenuesSlide')}</p>
				</>
			),
			placement: 'right',
			//disableBeacon: true,
			target: '.menuDrawerRevenues',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.teamSlides')}</p>
				</>
			),
			placement: 'bottom',
			//disableBeacon: true,
			target: '.testTeamClass',
			spotlightClicks: true,
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.projectSlides')}</p>
				</>
			),
			placement: 'bottom',
			disableBeacon: true,
			target: '.testProjectClass',
			spotlightClicks: true,
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.budgetSlides')}</p>
				</>
			),
			placement: 'bottom',
			disableBeacon: true,
			target: '.createQuoteButton',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.budgetList')}</p>
				</>
			),
			placement: 'bottom',
			disableBeacon: true,
			target: '.testQuotesClass',
		},
		{
			content: (
				<>
					<p>{tAdminwalkthrough('steps.finalSlide')}</p>
				</>
			),
			placement: 'center',
			disableBeacon: true,
			target: 'body',
		},
	]

	interface TourState {
		run: boolean
		steps: Step[]
		stepIndex: number
		isUserMenuOpen: boolean
	}

	const [tourState, setTourState] = useState<TourState | null>()
	const defaultTourState = {
		run: true,
		stepIndex: 0,
		// steps: newSteps,
		isUserMenuOpen: false,
	}

	useEffect(() => {
		// Getting and setting browser language, need to do it here since we set up langugage in auth redux(check autentication status) to the user profile language
		const userLang = navigator.language
		i18n.changeLanguage(userLang ?? 'fr')
	}, [])

	// biome-ignore lint: Legitimate effect responding to admin tour state changes
	useEffect(() => {
		if (adminTour) {
			setTourState({ ...adminTour, steps: newSteps })
		} else {
			if (pathname === '/clients') {
				setTourState({
					...defaultTourState,
					steps: newSteps,
				})
			}
		}
	}, [adminTour])

	// Update steps when language changes (steps contain translated content)
	// biome-ignore lint: language is the trigger, newSteps will have correct translations at that time
	useEffect(() => {
		setTourState((prev) => (prev ? { ...prev, steps: newSteps } : prev))
	}, [language])
	// Used to start the tour again when clicking prev from step 7
	// biome-ignore lint: Legitimate effect responding to menu state changes
	useEffect(() => {
		if (isMenuDrawerRevenuesOpen && tourState?.stepIndex === 7) {
			const updateDefaultTourState = {
				...defaultTourState,
				run: true,
				stepIndex: 6,
			}
			dispatch(setAdminTour(updateDefaultTourState))
		}
	}, [tourState?.stepIndex, isMenuDrawerOpen, isMenuDrawerRevenuesOpen])

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { status, type, index, action } = data
		const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

		if (finishedStatuses.includes(status)) {
			const updateDefaultTourState = {
				...defaultTourState,
				run: false,
				stepIndex: 0,
			}
			i18n.changeLanguage(userInformation?.userLanguage ?? 'fr')
			dispatch(setAdminTour(updateDefaultTourState))
			// Close menu on skip
			dispatch(setIsUserMenuOpen(false))
			dispatch(setIsMenuDrawerOpen(false))
			dispatch(setIsMenuDrawerBudgetingOpen(false))
			dispatch(setIsMenuDrawerExpensesOpen(false))
			dispatch(setIsMenuDrawerRevenuesOpen(false))
			dispatch(setIsAdminWalkthroughClosed(true))
			if (status === STATUS.FINISHED) {
				// To show confetti after the tour
				// setShowConfetti(true)
				// If confetti is to be shown on last slide
				setShowConfetti(false)
			}
			if (dontShowTour) {
				//! Change to our boolean
				dispatchThunk(updateUserInfo({ hideTour: true }))
			}
		} else if (
			([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(
				type
			)
		) {
			const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1)
			if (index === 1 && action === ACTIONS.NEXT) {
				// if user modal is open and we move
				dispatch(setIsUserMenuOpen(true))
			}
			if (index === 2) {
				dispatch(setIsUserMenuOpen(false))
				if (action === ACTIONS.NEXT) {
					dispatch(setIsMenuDrawerOpen(true))
					dispatch(setIsMenuDrawerBudgetingOpen(true))
					dispatch(setIsMenuDrawerExpensesOpen(true))
					dispatch(setIsMenuDrawerRevenuesOpen(true))
				}
			}
			if (index === 3 && action === ACTIONS.PREV) {
				// If user modal is closed and we want to move open from next or prev
				dispatch(setIsUserMenuOpen(true))
				dispatch(setIsMenuDrawerOpen(false))
				dispatch(setIsMenuDrawerBudgetingOpen(false))
				dispatch(setIsMenuDrawerExpensesOpen(false))
				dispatch(setIsMenuDrawerRevenuesOpen(false))
			}

			if (index === 6 && action === ACTIONS.NEXT) {
				// If confetti is to be shown on last slide
				dispatch(setIsMenuDrawerOpen(false))
				dispatch(setIsMenuDrawerBudgetingOpen(false))
				dispatch(setIsMenuDrawerExpensesOpen(false))
				dispatch(setIsMenuDrawerRevenuesOpen(false))
			}
			if (index === 7 && action === ACTIONS.PREV) {
				dispatch(setIsMenuDrawerOpen(true))
				dispatch(setIsMenuDrawerBudgetingOpen(true))
				dispatch(setIsMenuDrawerExpensesOpen(true))
				dispatch(setIsMenuDrawerRevenuesOpen(true))
				// Need to return and end the tour, then start it using line 321
				const updateDefaultTourState = {
					...defaultTourState,
					run: false,
				}
				dispatch(setAdminTour(updateDefaultTourState))
				return
			}

			if (index === 8 && action === ACTIONS.PREV) {
				// If confetti is to be shown on last slide
				dispatch(
					setAdminTour({
						run: false,
						stepIndex: 7,
						isUserMenuOpen: false,
					})
				)
				navigate('/clients')
				return
			}
			if (index === 9 && action === ACTIONS.NEXT) {
				if (fetchProjectChildrenApiStatus !== 'succeeded') {
					dispatch(
						setAdminTour({
							run: false,
							stepIndex: 10,
							isUserMenuOpen: false,
						})
					)
					return
				}
			}
			if (index === 9 && action === ACTIONS.PREV) {
				const testQuote = projectChildrenData?.find(
					(p) => p.name === 'DQJSM'
				)
				if (testQuote) {
					dispatch(
						setAdminTour({
							run: false,
							stepIndex: 8,
							isUserMenuOpen: false,
						})
					)
					navigate(`/clients/${testQuote?.parentTeamId}`)
					return
				}
			}
			if (index === 10 && action === ACTIONS.NEXT) {
				// If confetti is to be shown on last slide
				setShowConfetti(true)
			}
			if (index === 11 && action === ACTIONS.PREV) {
				setShowConfetti(false)
			}

			// if (isUserMenuOpen) {
			//   setTourState({ ...tourState, stepIndex: nextStepIndex })
			//   console.log('isUserMenuOpen: ', isUserMenuOpen)
			// }

			const updateDefaultTourState = {
				...defaultTourState,
				stepIndex: nextStepIndex,
			}
			dispatch(setAdminTour(updateDefaultTourState))
		}
	}
	return (
		<div>
			{(isTeamsPage || isProjectsPage) &&
				isAdmin &&
				!hideTour &&
				!isSmall &&
				tourState && (
					<Joyride
						callback={handleJoyrideCallback}
						continuous
						hideCloseButton
						run={tourState.run}
						// Enable to  make elements clickable
						// spotlightClicks={true}
						scrollToFirstStep
						showProgress
						showSkipButton
						steps={tourState.steps}
						stepIndex={tourState.stepIndex}
						tooltipComponent={(props) => (
							<WalkthroughTooltip
								props={props}
								dontShowTour={dontShowTour}
								handleChange={handleChange}
								disableNextStepsArray={[7, 8]}
							/>
						)}
						styles={{
							options: {
								zIndex: 3000,
							},
						}}
						disableOverlayClose
						disableCloseOnEsc
					/>
				)}
			{showConfetti && (
				<Confetti
					numberOfPieces={200}
					// onConfettiComplete={(confetti: any) => {
					//   console.log('a:', confetti)
					//   return setShowConfetti(false)
					// }}
					tweenDuration={10000}
					style={{ zIndex: 10000 }}
					// recycle={false}
				/>
			)}
		</div>
	)
}

export default AdminWalkthrough
